<script setup>
import { useAuthStore } from '@lib/pp-shop-lib/stores';
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useStorage } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const { locale } = useI18n({ useScope: 'global' });
const localeStorage = useStorage('locale', 'de-DE');
const supportedLocales = ['de-DE', 'en-US'];

if (!supportedLocales.includes(localeStorage.value)) localeStorage.value = 'de-DE'
if (supportedLocales.includes(localeStorage.value)) locale.value = localeStorage.value

const showInfo = ref(false)
const cartDropdown = ref(null)
const accountDropdown = ref(null)
const { user } = storeToRefs(authStore);

const selectedLocale = computed({
    get() {
        return locale.value
    },
    set(newLocale) {
        if (!supportedLocales.includes(newLocale)) return
        locale.value = newLocale
        localeStorage.value = newLocale
    }
})

const isCartRoute = computed(() => route.path.startsWith('/shop/cart') || route.path.startsWith('/shop/checkout'))
const isAccountRoute = computed(() => route.path.startsWith('/shop/account') && !route.path.startsWith('/shop/account/usermedia'))

function hideDropdown(dropdownElement) {
    const dropdown = window.UIkit?.dropdown(dropdownElement)
    dropdown?.hide(false)
}

async function onLogoutClick() {
    await authStore.logout()
    hideDropdown(cartDropdown.value)
    hideDropdown(accountDropdown.value)
    router.replace('/shop/account/login')
}

watch(() => route.fullPath, () => {
    hideDropdown(cartDropdown.value)
    hideDropdown(accountDropdown.value)
})

</script>

<template>
    <div class="nav-brand">
        <router-link class="logo" to="/shop" aria-label="Startseite">
            <span class="logo-image" aria-hidden="true"></span>
        </router-link>
    </div>
    <nav id="main-nav" uk-navbar class="uk-navbar-container">

        <div class="uk-navbar-center">

            <ul class="uk-navbar-nav">
                <li>
                    <div class="uk-inline uk-margin-top">
                        <a class="uk-button uk-margin-small-top uk-button-text" type="button">
                        <router-link to="/shop"><i uk-icon="icon: home"></i>{{ $t('nav.home') }}</router-link>
                    </a>
                    </div>
                </li>
                <li>
                    <div class="uk-inline uk-margin-top">
                        <a class="uk-button uk-margin-small-top uk-button-text" type="button">
                        <router-link to="/shop/account/usermedia"><i uk-icon="icon: image"></i>{{ $t('nav.images') }}</router-link>
                    </a>
                    </div>
                </li>
                <li>
                    <div class="uk-inline uk-margin-top">
                    <a class="uk-button uk-margin-small-top uk-button-text" :class="{ active: isCartRoute }" type="button"><i
                                uk-icon="icon: cart"><span id="pp-cart-bubble"></span></i>{{ $t('nav.cart') }}</a>
                        <div ref="cartDropdown" class="nav-drop" uk-dropdown="mode: click">
                        <li>
                            <div class="pp-cart-info"></div>
                        </li>
                        <router-link to="/shop/cart"><i uk-icon="icon: cart"></i>
                            <div v-show="showInfo" class=""></div> {{ $t('nav.toCart') }}
                        </router-link>
                        </div>
                    </div>
                </li>
                <li>
                    <div class="uk-inline uk-margin-top">
                    <a class="uk-button uk-margin-small-top uk-button-text" :class="{ active: isAccountRoute }" type="button"><i class="nav-icon"
                                uk-icon="icon: user"><span id="pp-account-bubble"></span></i>{{ $t('nav.account') }}</a>
                        <div ref="accountDropdown" class="nav-drop" uk-dropdown="mode: click">
                            <li v-if="!user"><router-link to="/shop/account/login"><i uk-icon="icon: sign-out"></i>{{ $t('nav.login') }}</router-link></li>
                            <li v-if="user"><router-link to="/shop/account/addresses"><i uk-icon="icon: location"></i>{{ $t('accountViews.addresses') }}</router-link></li>
                            <li v-if="user"><router-link to="/shop/account/accounting"><i uk-icon="icon: bag"></i>{{ $t('accountViews.bookings') }}</router-link></li>
                            <li v-if="user"><router-link to="/shop/account/orders"><i uk-icon="icon: bag"></i>{{ $t('accountViews.orders') }}</router-link></li>
                            <li v-if="user"><router-link to="/shop/account/settings"><i uk-icon="icon: cog"></i>{{ $t('accountViews.settings') }}</router-link></li>
                            <li v-if="user" class="nav-drop-logout"><a @click.prevent="onLogoutClick" href="#"><i uk-icon="icon: sign-out"></i>{{ $t('nav.logout') }}</a></li>
                        </div>
                    </div>
                </li>
        </ul>
        </div>
        <div class="uk-navbar-right nav-lang-item">
            <div class="uk-inline uk-margin-top nav-lang">
                <label class="lang-label" for="main-nav-locale">{{ $t('language') }}</label>
                <select id="main-nav-locale" v-model="selectedLocale" class="uk-select lang-select">
                    <option value="de-DE">Deutsch</option>
                    <option value="en-US">English</option>
                </select>
            </div>
        </div>
    </nav>
</template>

<style lang="scss">
#main-nav {
    background: linear-gradient(to left, #0F7AE5, #53bafa);
    color: #fff;
    text-shadow: none;
    position: relative;
    min-height: 80px;
}

.nav-brand {
    position: absolute;
    top: 8px;
    left: 12px;
    z-index: 1002;
}

.nav-brand .logo {
    display: inline-flex;
    align-items: center;
    height: 46px;
    text-decoration: none;
}

.nav-brand .logo-image {
    display: block;
    width: 180px;
    height: 26px;
    background-image: var(--nav-logo-url);
    background-repeat: no-repeat;
    background-position: left center;
    background-size: contain;
}

#main-nav li a {
    color: #fff;
}

#main-nav .uk-button a:hover {
    text-decoration: none;
}

#main-nav li a.active {
    color: #f3e5a8;
    font-weight: bold
}

#main-nav .uk-button.active {
    color: #f3e5a8;
    font-weight: bold;
}

#main-nav .uk-button.active::before {
    right: 0;
}

#main-nav div.pp-cart-info {


    margin-bottom: 10px;

}

#main-nav .nav-drop a {
    color: #666;
}

#main-nav .nav-drop li > a {
    display: flex;
    align-items: center;
}

#main-nav .nav-drop li > a i {
    margin-right: 5px;
}

#main-nav .nav-drop .nav-drop-logout {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e6e6e6;
}

#main-nav .uk-button i {
    margin-right: 5px;
}

#main-nav .nav-lang {
    display: flex;
    align-items: center;
    gap: 4px;
    position: relative;
    top: 0;
}

#main-nav .lang-label {
    color: #fff;
    font-size: 11px;
}

#main-nav .lang-select {
    height: 21px;
    min-height: 21px;
    line-height: 19px;
    font-size: 12px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    background-color: #fff;
    color: #333;
    padding: 0 20px 0 6px;
}

#main-nav .nav-lang-item {
    position: relative;
    z-index: 1001;
    margin-right: 8px;
    align-self: flex-start;
}

#main-nav .nav-lang-item .uk-margin-top {
    margin-top: 0 !important;
}

@include sxs {
    #main-nav {
        min-height: 88px;
    }

    #main-nav .uk-navbar-center {
        width: 100%;
    }

    #main-nav .uk-navbar-nav {
        width: 100%;
        height: auto !important;
        min-height: 88px;
        display: flex;
        flex-wrap: nowrap;
        justify-content: space-between;
        align-items: center;
        position: relative;
        box-sizing: border-box;
        padding: 30px 8px 8px;
    }

    #main-nav .uk-navbar-nav > li {
        display: flex;
        align-items: center;
    }

    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) {
        flex: 1 1 0;
        justify-content: center;
    }

    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) > .uk-inline {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) > .uk-inline > .uk-button {
        font-size: 12px;
        line-height: 1.2;
        min-height: 44px;
        width: 100%;
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        white-space: normal;
        text-align: center;
    }

    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) > .uk-inline > .uk-button > a {
        display: inline-block;
        width: 100%;
        line-height: 1.2;
        white-space: normal;
        text-align: center;
    }

    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) > .uk-inline > .uk-button i,
    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) > .uk-inline > .uk-button .uk-icon {
        display: block;
        margin-right: 0;
        margin-bottom: 2px;
        font-size: 30px;
        line-height: 1;
    }

    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) > .uk-inline > .uk-button .uk-icon svg,
    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) > .uk-inline > .uk-button i svg {
        width: 30px;
        height: 30px;
    }

    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) .uk-margin-top,
    #main-nav .uk-navbar-nav > li:not(.nav-lang-item) .uk-margin-small-top {
        margin-top: 0 !important;
    }

    #main-nav .nav-lang-item {
        position: relative;
        z-index: 1001;
        margin-right: 8px;
        align-self: flex-start;
    }

    #main-nav .nav-lang-item .uk-margin-top {
        margin-top: 0 !important;
    }

    #main-nav .nav-lang {
        top: 0;
        gap: 4px;
    }

    #main-nav .lang-label {
        font-size: 11px;
    }

    #main-nav .lang-select {
        height: 21px;
        min-height: 21px;
        line-height: 19px;
        font-size: 12px;
        padding: 0 20px 0 6px;
    }
}
</style>